/**
 * The curtain over a service-worker update (§7).
 *
 * `registerType: 'autoUpdate'` reloads the tab the moment a new worker activates, and that
 * lands on a player who is already reading this week's match: the schedule blinks out, the
 * window paints `--bg`, and the app boots a second time through "Loading the schedule…".
 * Detecting the new version earlier is not on the table — detection *is* a network fetch of
 * `sw.js`, and the shell paints from the precache long before that resolves — so the reload
 * is covered instead of avoided. One screen is raised before the reload, restored from
 * sessionStorage on the way back up, and dropped only once there is real content behind it,
 * which turns two loads into one continuous wait.
 *
 * The markup lives in `index.html` rather than in the React tree precisely so it can paint on
 * the first frame after the navigation, before this bundle has parsed. Both sides of the
 * reload therefore show the same pixels.
 */

/** Survives the reload but not the app being reopened, which is exactly the lifetime wanted. */
const FLAG = 'topspin.updating';

/** Long enough for the curtain to paint and the line to be read, short enough not to feel like
 *  a stall. The new worker has already activated by this point — this delays only the reload. */
const REVEAL_MS = 700;

/** If the second boot never settles — offline, a slow cold API — the curtain still comes down
 *  and the app underneath (its own loading or offline screen) is left to say so. */
const SETTLE_CAP_MS = 6000;

/** Matches the opacity transition in `styles.css`. */
const FADE_MS = 200;

function curtain(): HTMLElement | null {
  return document.getElementById('update-curtain');
}

/** sessionStorage throws outright in some private-browsing modes; losing the flag only costs
 *  the curtain on the far side of the reload, so it is never worth failing the update over. */
function flagged(): boolean {
  try {
    return sessionStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

function setFlag(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(FLAG, '1');
    else sessionStorage.removeItem(FLAG);
  } catch {
    /* see above */
  }
}

function raise(): void {
  const el = curtain();
  if (!el) return;
  el.removeAttribute('data-leaving');
  el.hidden = false;
}

function drop(): void {
  const el = curtain();
  if (!el || el.hidden) return;
  // Fades rather than cuts: the schedule snapping in would read as a third load.
  el.dataset.leaving = '';
  window.setTimeout(() => {
    el.hidden = true;
    el.removeAttribute('data-leaving');
  }, FADE_MS);
}

/** `onNeedReload` from `registerSW` — the same reload autoUpdate would have done unprompted,
 *  held back just long enough to say what is happening. */
export function reloadForUpdate(): void {
  setFlag(true);
  raise();
  window.setTimeout(() => window.location.reload(), REVEAL_MS);
}

/** Called on boot. The inline script in `index.html` has normally raised the curtain already;
 *  this adopts it, and arms the cap so it cannot outlive the load it is covering. */
export function resumeUpdate(): void {
  if (!flagged()) return;
  raise();
  window.setTimeout(finishUpdate, SETTLE_CAP_MS);
}

/** Drop the curtain once there is a screen behind it. Safe to call when none was raised. */
export function finishUpdate(): void {
  setFlag(false);
  drop();
}
